import type { MovementError } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import type { CompleteMovementPayload } from "@services/movement-services/types";
import { completeMovementById } from "@services/movementServicesApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

const getCompleteMovementAction = (payload: CompleteMovementPayload): string => {
  if (payload.status?.includes("APPROVED")) return "approve_movement_service";
  if (payload.status?.includes("REJECTED")) return "reject_movement_service";
  if (payload.status?.includes("DISPUTED")) return "dispute_movement_service";
  return "complete_movement_service";
};

export const useCompleteMovement = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess: () => void;
  handleError: (error: MovementError) => void;
}) => {
  const completeMovementMutation = useMutation({
    mutationFn: (variables: Parameters<typeof completeMovementById>[0]) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "MovementService",
          action: getCompleteMovementAction(variables.payload),
          entityType: "movement_service",
        },
        (transactionId) =>
          completeMovementById({ ...variables, ...(transactionId ? { transactionId } : {}) }),
      ),
    onSuccess: () => handleSuccess(),
    onError: (error: MovementError) => handleError(error),
  });

  const completeMovement = async ({
    id,
    service,
    payload,
  }: {
    id: string;
    service: string;
    payload: CompleteMovementPayload;
  }) => {
    completeMovementMutation.mutate({
      id,
      service,
      payload,
    });
  };

  return {
    completeMovement,
  };
};
